import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../api/client'

/** One open contract-quality ticket (domain `contrat`, spec 2026-07-31-01). */
export type ContractQualityIssue = {
    uid: string
    subtype?: string | null
    title: string
    priority: number
    target_entity_uid?: string | null
}

// The org-wide open ticket count is expected in the tens; walk pages up to
// this bound so the badge column never silently misses a contract.
const MAX_PAGES = 10
const PAGE_SIZE = 100

/**
 * Fetches every open ticket of the `contrat` domain for the org and indexes
 * them by target contract UID — drives the "À fiabiliser" badge on the
 * contracts table and detail page.
 */
export function useContractQualityIssues(org: string) {
    const { data, isLoading } = useQuery({
        queryKey: ['contract-quality-issues', org],
        queryFn: async () => {
            const all: ContractQualityIssue[] = []
            for (let page = 1; page <= MAX_PAGES; page++) {
                const { data: res, error } = await apiClient.GET(
                    '/api/v1/organizations/{orgCode}/issues',
                    {
                        params: {
                            path: { orgCode: org },
                            query: {
                                domain: 'contract',
                                status: 'open,in_progress',
                                assignee_uid: 'all',
                                type: '*',
                                target_entity_type: 'contract',
                                limit: PAGE_SIZE,
                                page,
                            },
                        },
                    },
                )
                if (error) throw error
                const rows = ((res as any)?.data ?? []) as ContractQualityIssue[]
                all.push(...rows)
                const total = (res as any)?.total ?? all.length
                if (rows.length === 0 || all.length >= total) break
            }
            return all
        },
        staleTime: 60_000,
    })

    const byContract = useMemo(() => {
        const map: Record<string, ContractQualityIssue[]> = {}
        for (const issue of data ?? []) {
            if (!issue.target_entity_uid) continue
            ;(map[issue.target_entity_uid] ??= []).push(issue)
        }
        return map
    }, [data])

    return { byContract, isLoading }
}
